import { useState, useEffect, useRef } from 'react';
import './App.css';
import pfpImage from './assets/pfp.png';
import ghWhite from './assets/ghWhite.svg'
import ghBlack from './assets/ghBlack.svg'
import linkBlack from './assets/linkBlack.svg'
import linkWhite from './assets/linkWhite.svg'
import fsm from './assets/fsm.png'
import web from './assets/web.png'
import webFull from './assets/webFull.png'
import dbms from './assets/dbms.png'
import dbmsFull from './assets/dbmsFull.png'
import stock from './assets/stock.png'
import filter from './assets/filter.png'
import artdoro from './assets/artdoro.png'
import smarterStudy from './assets/smarterStudy.png'

const projects = [
  {
    id: 1,
    title: "ML-Enhanced Control System for Cardiovascular Phantom Blood Pressure Simulations",
    description: "Cut system calibration time by 99.17%. 2nd place, Computational & Data Sciences undergraduate division, Texas A&M Student Research Week. Presented at 2 research symposiums. Publishing undergraduate thesis. Python, C, C++.",
    image: fsm,
    links: [
      { key: "Link", url: "https://oaktrust.library.tamu.edu/items/c20b82aa-a560-4258-8d26-0857f56d7235" },
    ],
  },
  {
    id: 2,
    title: "Flux DBMS",
    description: "Control and monitoring software for distributed battery management system of Texas A&M Formula Electric. C#.",
    image: dbms,
    lightboxImage: dbmsFull,
    links: [],
  },
  {
    id: 3,
    title: "False Ticket Detection System",
    description: "ML-based anomaly detection system leveraging TF-IDF vectorization and HBDSCAN clustering developed at Fidelity Investments. Reduced false ticket volume by 40%. Saving 150+ hours of manual triage on average per week. Python.",
    links: [],
  },
  {
    id: 4,
    title: "Phantom Web",
    description: "Full-stack web application developed for Texas A&M's Optical Bio-Sensing Laboratory. Support remote access and control of lab phantom system. Web sockets for live monitoring and plotting. Deployed on AWS. Python, C, C++.",
    image: web,
    lightboxImage: webFull,
    links: [],
  },
  {
    id: 5,
    title: "Quantitative Stock Predictor",
    description: "Financial market close price predictor attaining 0.019% average error. Python.",
    image: stock,
    links: [
      { key: "Github", url: "https://github.com/RajNallanthighal/SVR-Stock-Predictor" },
      { key: "Link", url: "https://rajnallanthighal.pythonanywhere.com/" },
    ],
  },
  {
    id: 6,
    title: "Fabulous Filter",
    description: "Modifiable high-order bandpass filter audio plugin designed for steep attenuation. C++.",
    image: filter,
    links: [
      { key: "Github", url: "https://github.com/RajNallanthighal/Fabulous-Filter" }
    ],
  },
];

const trinkets = [
  {
    id: 1,
    title: "Artdoro",
    description: "I hated the constant ticking of my timer while I worked. Artdoro unveils a piece from the MoMA while I work; a respite for my eyes instead of an officer of routine.",
    image: artdoro,
    links: [
      { key: "Demo", url: "https://artdoro.vercel.app/" },
    ],
  },
  {
    id: 2,
    title: "Smarter Study",
    description: "I often find myself distracted or daydreaming when I'm supposed to be re-reading my notes. Smarter Study keeps me engaged, helping me actively recall information instead of reading the same paragraph twice.",
    image: smarterStudy,
    links: []
  },
  {
    id: 3,
    title: "Push Assist",
    description: "Todos ebb and flow throughout the day, and my trusty Google Tasks system was failing to help me easily manage deadlines and priorities. Push Assist keeps me on track, reminding me what matters most for every precious minute.",
    links: []
  },
];

const IconButton = ({ href, label, defaultSrc, hoverSrc }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="project-link-btn"
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img src={hovered ? hoverSrc : defaultSrc} alt={label} width="16" height="16" />
    </a>
  );
};

const App = () => {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [openTrinkets, setOpenTrinkets] = useState(false);

  // Track which pane is visible so we can hide the arrow on the last pane
  const [activePane, setActivePane] = useState(0);
  const containerRef = useRef(null);
  const totalPanes = 4;

  const prev = () => setCurrent((c) => (c - 1 + projects.length) % projects.length);
  const next = () => setCurrent((c) => (c + 1) % projects.length);

  // Close lightbox and trinkets drawer on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setLightbox(false);
        setOpenTrinkets(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Preload images
  useEffect(() => {
    projects.forEach(p => {
      if (p.image) new Image().src = p.image;
      if (p.lightboxImage) new Image().src = p.lightboxImage;
    });
    trinkets.forEach(t => {
      if (t.image) new Image().src = t.image;
    });
  }, []);

  // Detect which pane is active via scroll position
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const index = Math.round(el.scrollTop / el.clientHeight);
      setActivePane(index);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Issue 2: Prevent overscroll/rubber-band past the top on mobile
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const preventOverscroll = (e) => {
      // If we're at the top and trying to scroll up, block it
      if (el.scrollTop === 0 && e.touches[0].clientY > 0) {
        // Store starting touch position
        el._touchStartY = e.touches[0].clientY;
      }
    };
    const blockUpScroll = (e) => {
      if (el.scrollTop <= 0) {
        const dy = e.touches[0].clientY - (el._touchStartY || 0);
        if (dy > 0) e.preventDefault();
      }
    };
    el.addEventListener('touchstart', preventOverscroll, { passive: true });
    el.addEventListener('touchmove', blockUpScroll, { passive: false });
    return () => {
      el.removeEventListener('touchstart', preventOverscroll);
      el.removeEventListener('touchmove', blockUpScroll);
    };
  }, []);

  const isLastPane = activePane >= totalPanes - 1;

  return (
    <div className="container" ref={containerRef}>

      {/* Pane 1: Hero */}
      <section id="home" className="pane hero">
        <div className="image-container fade-in">
          <img src={pfpImage} alt="Raj Nallanthighal" className="profile-img"/>
        </div>
        <h1 className="fade-in delay-1">Raj Nallanthighal</h1>
        <p className="subtitle fade-in delay-2">
          Software Developer Fall Intern at IBM
          <br/>
          Ex Goldman Sachs, Fidelity
          <br />
          Honors CS at Texas A&M
        </p>
      </section>

      {/* Pane 2: Current Focus */}
      <section id="current" className="pane focus">
        <div className="card fade-in">
          <h2>Hi, I'm Raj.</h2>
          <div className="content">
            <p>I'm a Senior at Texas A&M studying Computer Science with minors in Neuroscience and Business. My technical interests lie in system design, low-latency systems, AI/ML, data platforms, and embedded software.</p>
            <p>I believe that software is most meaningful when it helps people do their best work, and that drives most of what I build.</p>
            <p>Previously, I've worked as an Engineering Summer Analyst at Goldman Sachs in the Data Engineering division, and a Software Engineering Intern at Fidelity Investments in their ML/AI Ops team. I've recently completed my undergraduate thesis, developing a machine learning-enhanced control system for cardiovascular phantom simulations. Additionally, I'm building a distributed battery control application for the Texas A&M Formula Electric team, and freelancing through DataAnnotation.</p>
            <p>Away from my computer, I love to play golf, lift weights, find new music, and I've been having a lot of fun getting into bouldering and learning classical guitar.</p>
          </div>
        </div>
      </section>

      {/* Pane 3: Projects Carousel */}
      <section id="projects" className="pane projects">
        <div className="carousel-wrapper fade-in delay-1">
          <button className="arrow arrow-left" onClick={prev} aria-label="Previous project">&#8592;</button>

          <div className={`project-card ${!projects[current].image ? 'no-image' : ''}`}>
            <div className="project-text">
              <h3>{projects[current].title}</h3>
              <p>{projects[current].description}</p>
              <div className="project-footer">
                <span className="project-counter">{current + 1} / {projects.length}</span>
                {projects[current].links.length > 0 && (
                  <div className="project-links">
                    {projects[current].links.map((link) => (
                      <IconButton
                        key={link.key}
                        href={link.url}
                        label={link.key}
                        defaultSrc={link.key === "Github" ? ghBlack : linkBlack}
                        hoverSrc={link.key === "Github" ? ghWhite : linkWhite}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            {projects[current].image && (
              <>
                {/* Issue 3: contain instead of cover, with a muted background */}
                <div className="project-image" onClick={() => setLightbox(true)}>
                  <img src={projects[current].image} alt={projects[current].title} />
                </div>
                <div
                  className={`lightbox ${lightbox ? 'lightbox-open' : ''}`}
                  onClick={() => setLightbox(false)}
                >
                  <img
                    src={projects[current].lightboxImage || projects[current].image}
                    alt={projects[current].title}
                  />
                </div>
              </>
            )}
          </div>

          <button className="arrow arrow-right" onClick={next} aria-label="Next project">&#8594;</button>
        </div>
      </section>

      <section id="connect" className="pane connection">
        <div className="social-grid fade-in">
          <a href="https://www.linkedin.com/in/rajnallanthighal/" target="_blank" rel="noreferrer" className="grid-item">
            <span className="label">Connect</span>
            <span className="link-text">LinkedIn</span>
          </a>
          <a href="mailto:raj.nallanthi@gmail.com" target="_blank" rel="noreferrer" className="grid-item">
            <span className="label">Inquire</span>
            <span className="link-text">Email</span>
          </a>
        </div>
        <footer className="footer fade-in delay-1">© 2026 Raj Nallanthighal</footer>
      </section>

      {activePane === 2 && !openTrinkets && (
        <button
          className="trinkets-button"
          onClick={() => setOpenTrinkets(true)}
          aria-expanded={openTrinkets}
        >
          <span className="trinkets-arrow">→ </span>
          <i>Trinkets</i>
        </button>
      )}

      <aside className={`trinkets-sidebar ${openTrinkets ? 'trinkets-open' : ''}`}>
        <div className="trinkets-panel-header">
          <div>
            <p><i>A quiet collection of 10-minute projects for my own personal edification.</i></p>
          </div>
          <button className="trinkets-panel-close" onClick={() => setOpenTrinkets(false)} aria-label="Close trinkets sidebar">×</button>
        </div>
        <div className="trinkets-panel-list">
          {trinkets.map((item) => (
            <a
              key={item.id}
              className="trinket-item"
              href={item.links[0]?.url || '#'}
              target="_blank"
              rel="noreferrer"
            >
              {item.image && (
                <div className="trinket-thumb">
                  <img src={item.image} alt={item.title} className="trinket-thumb-img" />
                </div>
              )}
              <div className="trinket-item-title">{item.title}</div>
              <div className="trinket-item-line">{item.description}</div>
              <div className="trinket-number">{item.id} / {trinkets.length}</div>
            </a>
          ))}
        </div>
      </aside>

      {/* Issue 1: Scroll indicator arrow — hidden on the last pane */}
      <div className={`scroll-indicator ${isLastPane ? 'scroll-indicator--hidden' : ''}`} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 3v16M4 13l7 7 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

    </div>
  );
};

export default App;